import cs from 'classnames';
import type { ProgramTimelineContentObject } from 'models';
import { Button, Flex, RichTextRenderer, Row, Text, Title } from 'ui';
import { Translation } from 'features/translations';

type P = {
  item: ProgramTimelineContentObject;
  onCreateOrder: () => void;
};

const getContent = (item: ProgramTimelineContentObject) => {
  const htmlContent = (() => {
    switch (item.type) {
      case 'activity':
      case 'poll':
        /**
         * `dexcom_sync` is a sixth item shape with no corresponding backend schema or `item.type`
         * value anywhere in the FE or BE models; needs backend clarification. Preserved as-is with
         * a narrowly-scoped cast to keep current behavior.
         */
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- See comment above
        return (item as unknown as { dexcom_sync?: { html_content?: string } }).dexcom_sync?.html_content;
      case 'event':
        return item.event.htmlContent;
      case 'post':
        return item.post.htmlContent;
      case 'withings_order_post':
        return item.withings_order_post.htmlContent;
    }
  })();

  const objectId = (() => {
    switch (item.type) {
      case 'activity':
      case 'poll':
        return undefined;
      case 'event':
        return item.event.id;
      case 'post':
        return item.post.id;
      case 'withings_order_post':
        return item.withings_order_post.id;
    }
  })();

  const title = (() => {
    switch (item.type) {
      case 'activity':
      case 'event':
      case 'poll':
        return undefined;
      case 'post':
        return item.post.title ?? item.post.textContent;
      case 'withings_order_post':
        return item.withings_order_post.title ?? item.withings_order_post.textContent;
    }
  })();

  return { htmlContent, objectId, title };
};

const WithingsOrder = ({ item, onCreateOrder }: P) => {
  if (item.type !== 'withings_order_post') {
    return null;
  }

  if (item.filledWithingsOrder) {
    return <Text className="topic-item__order-completed">Order was successful!</Text>;
  }

  return (
    <Row align="middle" justify="center">
      <Button className="topic-item__order" onClick={onCreateOrder} size="large" type="primary">
        Create order
      </Button>
    </Row>
  );
};

/**
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
const LongTextContent = ({ item, onCreateOrder }: P) => {
  const { htmlContent, objectId, title } = getContent(item);

  if (!htmlContent) {
    return null;
  }

  return (
    <Flex className="topic-item__long-text" vertical>
      {title ? (
        <Title level={4} marginBottom={false}>
          {title}
        </Title>
      ) : null}
      <div
        className={cs('topic-item__long-text-wrapper', {
          'topic-item__long-text-wrapper--not-post': !['post', 'withings_order_post'].includes(item.type),
        })}
      >
        <RichTextRenderer data={htmlContent} />
      </div>
      <WithingsOrder item={item} onCreateOrder={onCreateOrder} />
      {objectId ? <Translation id={objectId} type={item.type} /> : null}
    </Flex>
  );
};

export default LongTextContent;
